import { ExtensionInstance } from '@mangovoice/types'

/**
 * Extension types that can be used as transfer targets.
 * Switchboards (ExtensionDialPlan) are excluded as they cannot receive transfers.
 */
const TRANSFERABLE_EXTENSION_TYPES = [
  'ExtensionLine',
  'ExtensionRingGroup',
  'ExtensionSharedVoicemailBox',
] as const

/**
 * Extension types excluded from Simple Dial (TransferAndContinue) nodes.
 * These types require specialized nodes (queue, conference_room, jump_to_dial_plan, etc.)
 */
const SIMPLE_DIAL_EXCLUDED_TYPES = [
  'ExtensionCallQueue',
  'ExtensionConferenceRoom',
  'ExtensionDialPlan',
  'ExtensionSharedVoicemailBox',
  'ExtensionSystem',
  'ExtensionVirtualFaxMachine',
] as const

/**
 * Filter extensions to only include those that can be used as transfer targets.
 * Excludes switchboard extensions (ExtensionDialPlan) since they cannot receive transfers.
 * Only includes active extensions (if active property is present).
 *
 * @param extensions - Array of extensions to filter
 * @returns Filtered array containing only active, transferable extensions
 */
export const getTransferableExtensions = (
  extensions: ExtensionInstance[] | undefined,
): ExtensionInstance[] => {
  if (!extensions) return []
  return extensions.filter((ext) => {
    // Filter by extension type
    const isTransferableType = TRANSFERABLE_EXTENSION_TYPES.includes(
      ext.type as (typeof TRANSFERABLE_EXTENSION_TYPES)[number],
    )
    if (!isTransferableType) return false

    // Filter by active status if the property exists (API may or may not include it)
    const extWithActive = ext as ExtensionInstance & { active?: boolean }
    if ('active' in extWithActive) {
      // If active property exists, only include active extensions
      return extWithActive.active === true
    }

    // If active property doesn't exist, include the extension (backward compatibility)
    return true
  })
}

/**
 * Filter extensions for Simple Dial (TransferAndContinue) nodes.
 * Excludes 6 extension types that require specialized nodes.
 * Matches legacy behavior: excludes CallQueue, ConferenceRoom, DialPlan, SharedVoicemailBox, System, VirtualFaxMachine.
 * Only includes active extensions for new nodes.
 *
 * @param extensions - Array of extensions to filter
 * @param selectedExtensionId - Optional selected extension ID (to include even if inactive)
 * @returns Filtered array matching Simple Dial requirements
 */
export const getSimpleDialExtensions = (
  extensions: ExtensionInstance[] | undefined,
  selectedExtensionId?: string,
): ExtensionInstance[] => {
  if (!extensions) return []
  return extensions.filter((ext) => {
    // Exclude specific extension types
    const isExcludedType = SIMPLE_DIAL_EXCLUDED_TYPES.includes(
      ext.type as (typeof SIMPLE_DIAL_EXCLUDED_TYPES)[number],
    )
    if (isExcludedType) {
      // Always include the selected extension even if it's an excluded type
      if (selectedExtensionId && String(ext.id) === selectedExtensionId) {
        return true
      }
      return false
    }

    // Filter by active status if the property exists
    const extWithActive = ext as ExtensionInstance & { active?: boolean }
    if ('active' in extWithActive) {
      // If active property exists, only include active extensions (or selected if inactive)
      if (selectedExtensionId && String(ext.id) === selectedExtensionId) {
        return true // Always include selected extension
      }
      return extWithActive.active === true // Only active for others
    }

    // If active property doesn't exist, include the extension (backward compatibility)
    return true
  })
}

/**
 * Filter extensions for Direct Line (Transfer) nodes.
 * Shows only ExtensionLine type extensions (UI-level filtering).
 * Matches legacy behavior: SQL returns all types, UI filters to ExtensionLine.
 * Only includes active extensions for new nodes.
 *
 * @param extensions - Array of extensions to filter
 * @param selectedExtensionId - Optional selected extension ID (to include even if inactive or wrong type)
 * @returns Filtered array containing only ExtensionLine extensions (or selected extension)
 */
export const getDirectLineExtensions = (
  extensions: ExtensionInstance[] | undefined,
  selectedExtensionId?: string,
): ExtensionInstance[] => {
  if (!extensions) return []
  return extensions.filter((ext) => {
    // Filter to only ExtensionLine type
    if (ext.type !== 'ExtensionLine') {
      // Always include the selected extension even if it's not ExtensionLine
      if (selectedExtensionId && String(ext.id) === selectedExtensionId) {
        return true
      }
      return false
    }

    // Filter by active status if the property exists
    const extWithActive = ext as ExtensionInstance & { active?: boolean }
    if ('active' in extWithActive) {
      // If active property exists, only include active extensions (or selected if inactive)
      if (selectedExtensionId && String(ext.id) === selectedExtensionId) {
        return true // Always include selected extension
      }
      return extWithActive.active === true // Only active for others
    }

    // If active property doesn't exist, include the extension (backward compatibility)
    return true
  })
}

/**
 * Filter extensions for Transfer Rollover nodes.
 * Shows only active ExtensionLine type extensions.
 * Matches legacy behavior: SQL filters to type = 'ExtensionLine' AND active = 1.
 * Most restrictive filtering - only active user phone lines.
 *
 * @param extensions - Array of extensions to filter
 * @param selectedExtensionId - Optional selected extension ID (to include even if inactive)
 * @returns Filtered array containing only active ExtensionLine extensions (or selected extension)
 */
export const getTransferRolloverExtensions = (
  extensions: ExtensionInstance[] | undefined,
  selectedExtensionId?: string,
): ExtensionInstance[] => {
  if (!extensions) return []
  return extensions.filter((ext) => {
    // Filter to only ExtensionLine type
    if (ext.type !== 'ExtensionLine') {
      // Always include the selected extension even if it's not ExtensionLine
      if (selectedExtensionId && String(ext.id) === selectedExtensionId) {
        return true
      }
      return false
    }

    // Filter by active status if the property exists
    const extWithActive = ext as ExtensionInstance & { active?: boolean }
    if ('active' in extWithActive) {
      // If active property exists, only include active extensions (or selected if inactive)
      if (selectedExtensionId && String(ext.id) === selectedExtensionId) {
        return true // Always include selected extension (even if inactive)
      }
      return extWithActive.active === true // Only active for others
    }

    // If active property doesn't exist, include the extension (backward compatibility)
    return true
  })
}

/**
 * Filter extensions for Leave Voicemail nodes.
 * Shows voicemail-enabled extensions: all ExtensionSharedVoicemailBox OR voicemail_enabled=true.
 * Matches legacy behavior: (voicemail_enabled = '1' || type = 'ExtensionSharedVoicemailBox').
 * Only includes active extensions for new nodes.
 *
 * @param extensions - Array of extensions to filter
 * @param selectedExtensionId - Optional selected extension ID (to include even if inactive)
 * @returns Filtered array matching Leave Voicemail requirements
 */
export const getVoicemailExtensions = (
  extensions: ExtensionInstance[] | undefined,
  selectedExtensionId?: string,
): ExtensionInstance[] => {
  if (!extensions) return []
  return extensions.filter((ext) => {
    // Filter by voicemail requirements
    // Include if: ExtensionSharedVoicemailBox OR voicemail_enabled=true
    const isSharedVoicemailBox = ext.type === 'ExtensionSharedVoicemailBox'

    // Check voicemail_enabled property (may not be in ExtensionInstance type)
    // Use unknown cast first to safely access property
    const extRecord = ext as unknown as Record<string, unknown>
    const voicemailEnabledValue = extRecord['voicemail_enabled']
    const isVoicemailEnabled =
      voicemailEnabledValue === true || voicemailEnabledValue === 1

    if (!isSharedVoicemailBox && !isVoicemailEnabled) {
      // Always include the selected extension even if it doesn't match voicemail requirements
      if (selectedExtensionId && String(ext.id) === selectedExtensionId) {
        return true
      }
      return false
    }

    // Filter by active status if the property exists
    const extWithActive = ext as ExtensionInstance & { active?: boolean }
    if ('active' in extWithActive) {
      // If active property exists, only include active extensions (or selected if inactive)
      if (selectedExtensionId && String(ext.id) === selectedExtensionId) {
        return true // Always include selected extension (even if inactive)
      }
      return extWithActive.active === true // Only active for others
    }

    // If active property doesn't exist, include the extension (backward compatibility)
    return true
  })
}

/**
 * Check if an extension type can be used as a transfer target.
 *
 * @param extensionType - The extension type to check
 * @returns True if the extension type is transferable
 */
export const isTransferableExtensionType = (extensionType: string): boolean => {
  return TRANSFERABLE_EXTENSION_TYPES.includes(
    extensionType as (typeof TRANSFERABLE_EXTENSION_TYPES)[number],
  )
}

/**
 * Get the switchboard display name from an extension.
 * Formats the display name according to legacy behavior:
 * - Returns "{name} - {number}" if number exists and is not already in the name
 * - Returns just "{name}" if number is already in name or number is empty
 * - Preserves original casing (no uppercase conversion)
 * - Handles pipe delimiters in names (displays as-is)
 *
 * Matches legacy PHP logic from my_switchboard.php:
 * extension_name + (number exists && number not in name ? " - " + number : "")
 *
 * @param extension - The extension instance (ExtensionDialPlan)
 * @returns Formatted display name (name + conditional extension number)
 */
export const getSwitchboardDisplayName = (
  extension: ExtensionInstance | undefined,
): string => {
  if (!extension) return ''

  const name = extension.name?.trim() || ''
  const number = extension.number?.trim() || ''

  // If no name, return empty string
  if (!name) return ''

  // Append extension number only if:
  // 1. Number exists (not empty after trim)
  // 2. Number is NOT already found in the name (case-sensitive check)
  if (number && !name.includes(number)) {
    return `${name} - ${number}`
  }

  // Return name as-is (number already in name, or no number)
  return name
}
